import React, { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { trackMarketingSitePageViewWithDeferredEvent } from "../../utils/analytics/marketing-site-screen-view";
import { useSearchParams } from "react-router-dom";
import { findLandingThemeById } from "./landingThemes";
import { LandingThemeProvider } from "./LandingThemeContext";
import { LandingLanguageProvider } from "./LandingLanguageContext";
import { ThemedLandingPage } from "./ThemedLandingPage";
import {
  buildLandingPageViewEvent,
  checkLandingVisitorWebSession,
  getForwardedTrackingParams,
  LandingVisitorAccountState,
  redirectToAppIfAlreadyLoggedIn,
  LANDING_VERSION_QUERY_PARAM,
  resolveAssignedLandingVariant,
  resolveLandingVisitorAccountStateForPageView,
} from "./handoff/appHandoff";
import { readAppStoreRouteOfferedOnThisPage, reportContinueInBrowserChosenBeforeHydration } from "./handoff/appStoreRoute";
import { MARKETING_LANGUAGES, MARKETING_LOCALES, MarketingLanguage } from "../../marketing-content/marketingLocales";
import { getMarketingTranslations } from "../../marketing-content/marketingTranslations";
import { buildLandingStructuredDataJsonLd } from "../../marketing-content/marketingStructuredData";
import { MarketingDocumentHead } from "../marketing-site/MarketingDocumentHead";

// Preview-only pieces load on demand so the production home page (Moss / Clearing) ships less JavaScript.
const OriginalLandingPage = React.lazy(() => import("./OriginalLandingPage"));
const VersionSwitcher = React.lazy(() =>
  import("./VersionSwitcher").then((module) => ({ default: module.VersionSwitcher }))
);

const VERSION_QUERY_PARAM = LANDING_VERSION_QUERY_PARAM;
// `?switcher=0` hides the comparison bar, e.g. for clean screenshots.
const HIDE_SWITCHER_QUERY_PARAM = "switcher";

const MARKETING_SITE_URL = "https://jungleai.com";
// Titles and descriptions carried over from the Framer home pages so search results do not change.
// The generated marketing catalogs supply the same localized title and description to React
// and scripts/generate-marketing-static-html.mjs.
const LANDING_SOCIAL_IMAGE_URL = `${MARKETING_SITE_URL}/images/marketing/social-link-preview.png`;

// Search-engine and social tags for the real home page. Helmet accepts an array of elements as
// children, which keeps the tags conditional without a fragment.
function buildLandingSeoTags(language: MarketingLanguage) {
  const seoCopy = { ...getMarketingTranslations(language).seo, canonicalUrl: `${MARKETING_SITE_URL}${MARKETING_LOCALES[language].home}` };
  return [
    <meta key="description" name="description" content={seoCopy.description} />,
    <link key="canonical" rel="canonical" href={seoCopy.canonicalUrl} />,
    ...MARKETING_LANGUAGES.map(locale => <link key={`hreflang-${locale}`} rel="alternate" hrefLang={MARKETING_LOCALES[locale].hreflang} href={`${MARKETING_SITE_URL}${MARKETING_LOCALES[locale].home}`} />),
    <link key="hreflang-default" rel="alternate" hrefLang="x-default" href={`${MARKETING_SITE_URL}/`} />,
    <meta key="og-type" property="og:type" content="website" />,
    <meta key="og-title" property="og:title" content={seoCopy.title} />,
    <meta key="og-description" property="og:description" content={seoCopy.description} />,
    <meta key="og-url" property="og:url" content={seoCopy.canonicalUrl} />,
    <meta key="og-image" property="og:image" content={LANDING_SOCIAL_IMAGE_URL} />,
    <meta key="twitter-card" name="twitter:card" content="summary_large_image" />,
    <meta key="og-site-name" property="og:site_name" content="Jungle" />,
    <script key="site-structured-data" type="application/ld+json">{buildLandingStructuredDataJsonLd({ ...seoCopy, htmlLanguage: MARKETING_LOCALES[language].htmlLang })}</script>,
  ];
}

// Code rebuild of the Framer landing page (jungleai.com), plus themed design versions for
// experimentation. `isProductionLanding` is the real marketing home page: visitors are split between
// the two designs under test, the comparison bar is hidden (`?switcher=1` shows it), and the page is
// indexable. Without it (the /testing preview) `?v=` picks a version and the bar shows. `htmlLang` is the
// page's `<html lang>`: "es" for the Spanish home page at /es.
export default function LandingPage({
  isProductionLanding = false,
  htmlLang = "en",
}: {
  isProductionLanding?: boolean;
  htmlLang?: MarketingLanguage;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const explicitVersionId = searchParams.get(VERSION_QUERY_PARAM);
  const assignedVariantId = useMemo(
    () => (isProductionLanding ? resolveAssignedLandingVariant() : null),
    [isProductionLanding]
  );
  const theme = findLandingThemeById(explicitVersionId ?? assignedVariantId);
  const isSwitcherHidden = isProductionLanding
    ? searchParams.get(HIDE_SWITCHER_QUERY_PARAM) !== "1"
    : searchParams.get(HIDE_SWITCHER_QUERY_PARAM) === "0";

  // One account lookup per page load, shared by the redirect and the page view. Started lazily from
  // browser-only callbacks, never during render, so prerendering and a double render make no request.
  // The /testing design preview is not marketing traffic and looks nothing up.
  const landingVisitorAccountCheckRef = useRef<Promise<LandingVisitorAccountState> | null>(null);
  const getLandingVisitorAccountCheck = useCallback(() => {
    landingVisitorAccountCheckRef.current ??= isProductionLanding
      ? checkLandingVisitorWebSession()
      : Promise.resolve<LandingVisitorAccountState>("no_known_account");
    return landingVisitorAccountCheckRef.current;
  }, [isProductionLanding]);

  useEffect(() => {
    // Someone who already has a Jungle session skips the marketing page, as on the Framer site.
    const controller = new AbortController();
    if (isProductionLanding) void redirectToAppIfAlreadyLoggedIn(controller.signal, getLandingVisitorAccountCheck());
    return () => controller.abort();
  }, [isProductionLanding, getLandingVisitorAccountCheck]);

  // Reported from react-helmet's callback so the page view carries this page's title, and so OpenPanel's
  // screen view goes out before "Landing Page Viewed" (see trackMarketingSitePageView). The /testing design
  // preview is not marketing traffic, so it skips the screen view but still reports the design viewed.
  //
  // The experiment counts prospective NEW users, so the product event waits for the account lookup: a
  // browser whose web session is linked to an account reports a separate event and stays out of "Landing
  // Page Viewed". Reporting it on render, before the lookup, counted every returning signed-in user just
  // before redirecting them. The screen view is NOT delayed (see the tracker).
  const reportLandingPageView = useCallback(() => {
    const forwardedTrackingParams = getForwardedTrackingParams();
    const pageViewEventProperties = {
      landingVariant: theme.id,
      isProductionLanding,
      lang: htmlLang,
      path: window.location.pathname,
      referrer: document.referrer || null,
      googleCampaignId: forwardedTrackingParams.get("gc_id"),
      tiktokCampaignId: forwardedTrackingParams.get("ttc_id"),
      utmSource: forwardedTrackingParams.get("utm_source"),
      // "ios" / "android" when this phone visitor from a Meta ad was shown the store links, else null.
      // Only "Landing Page Viewed" carries it (buildLandingPageViewEvent).
      appStoreRoute: readAppStoreRouteOfferedOnThisPage(),
    };
    trackMarketingSitePageViewWithDeferredEvent({
      deferredPageViewEvent: resolveLandingVisitorAccountStateForPageView(getLandingVisitorAccountCheck()).then(
        (landingVisitorAccountState) => buildLandingPageViewEvent({ landingVisitorAccountState, pageViewEventProperties })
      ),
      // Most visitors are new, and a real visitor must not vanish because the lookup was slow.
      pageViewEventIfNotResolvedInTime: buildLandingPageViewEvent({
        landingVisitorAccountState: "no_known_account",
        pageViewEventProperties,
      }),
      shouldRecordScreenView: isProductionLanding,
    });
    // A "Continue in browser" tapped before hydration, reported now that the view is (queued to go) out first.
    reportContinueInBrowserChosenBeforeHydration();
  }, [theme.id, isProductionLanding, htmlLang, getLandingVisitorAccountCheck]);

  const selectTheme = (themeId: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set(VERSION_QUERY_PARAM, themeId);
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <LandingThemeProvider theme={theme}>
      <MarketingDocumentHead language={htmlLang} onChangeClientState={reportLandingPageView}>
        <title>{getMarketingTranslations(htmlLang).seo.title}</title>
        {!isProductionLanding && <meta name="robots" content="noindex" />}
        {isProductionLanding && buildLandingSeoTags(htmlLang)}
      </MarketingDocumentHead>
      <Box
        data-marketing-page
        bg={theme.color.pageBg}
        color={theme.color.text}
        fontFamily={theme.font.body}
        minH="100vh"
        // Leave room for the fixed version switcher at the bottom of the page.
        pb={isSwitcherHidden ? 0 : "90px"}
        sx={theme.pageBackgroundLayer}
      >
        {/* Every section reads its copy in this language (useLandingCopy). */}
        <LandingLanguageProvider language={htmlLang}>
          {theme.id === "original" ? (
            <Suspense fallback={null}>
              <OriginalLandingPage language={htmlLang} />
            </Suspense>
          ) : (
            <ThemedLandingPage language={htmlLang} />
          )}
        </LandingLanguageProvider>
      </Box>
      {!isSwitcherHidden && (
        <Suspense fallback={null}>
          <VersionSwitcher activeTheme={theme} onSelectTheme={selectTheme} />
        </Suspense>
      )}
    </LandingThemeProvider>
  );
}
